import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { SplitHero } from "../components/SplitHero";
import { Intro } from "../components/Intro";
import { Marquee } from "../components/Marquee";
import { RevealLines, FadeUp } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";
import { Footer } from "../components/Footer";
import { useLang } from "../lib/i18n";
import { fetchProducts } from "../lib/api";

let introPlayed = false;

const Manifesto = () => {
  const { t } = useLang();
  return (
    <section data-testid="manifesto-section" className="bg-[var(--ink)] text-white px-5 md:px-10 py-28 md:py-44 grid grid-cols-12 gap-6">
      <p className="col-span-12 md:col-span-3 f-mono text-[11px] uppercase tracking-[0.3em] text-[var(--acid)]">(01) {t("manifestoKicker")}</p>
      <div className="col-span-12 md:col-span-9">
        <RevealLines lines={[t("manifesto1")]} className="f-street uppercase text-6xl md:text-[9rem] leading-[0.85]" />
        <RevealLines lines={[t("manifesto2")]} delay={0.15} className="f-serif italic text-6xl md:text-[8.5rem] leading-[0.95] text-[var(--ivory)] md:pl-[18%]" />
        <FadeUp delay={0.3} className="mt-14 md:mt-20 md:ml-[38%] max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed">
          {t("manifestoBody")}
        </FadeUp>
      </div>
    </section>
  );
};

const WorldTile = ({ to, img, title, text, street, testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const { t } = useLang();
  return (
    <Link ref={ref} to={to} data-testid={testid} className={`group relative block overflow-hidden h-[70vh] md:h-[88vh] ${street ? "bg-[var(--ink)]" : "bg-[var(--ivory)]"}`}>
      <div className="frame-clip absolute inset-0">
        <motion.img style={{ y, scale: 1.18 }} src={img} alt={title} className="w-full h-full object-cover" />
      </div>
      <div className={`absolute inset-0 ${street ? "bg-gradient-to-t from-black/80 via-black/10 to-transparent" : "bg-gradient-to-t from-[#1c140a]/60 via-transparent to-transparent"}`} />
      <div className="absolute bottom-8 left-6 right-6 md:bottom-12 md:left-10 md:right-10 text-white">
        <h3 className={street ? "f-street uppercase text-6xl md:text-8xl leading-[0.85]" : "f-serif italic text-6xl md:text-8xl leading-[0.95]"}>{title}</h3>
        <div className="mt-5 flex items-end justify-between gap-6">
          <p className="max-w-xs text-sm md:text-base text-white/90">{text}</p>
          <span className={`shrink-0 px-5 py-3 rounded-full text-[12px] f-mono uppercase tracking-[0.2em] transition-transform duration-500 group-hover:-translate-y-1 ${street ? "bg-[var(--acid)] text-black" : "bg-[var(--ivory)] text-[var(--navy)]"}`}>{t("discover")} →</span>
        </div>
      </div>
    </Link>
  );
};

const Worlds = () => {
  const { t } = useLang();
  return (
    <section data-testid="worlds-section" className="bg-[var(--ink)]">
      <div className="px-5 md:px-10 pb-10 flex justify-between items-end text-white">
        <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400">(02) {t("worlds")}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2">
        <WorldTile testid="world-tile-streetwear" street to="/collections/streetwear" img="/images/street_p3.jpg" title={t("street")} text={t("streetTile")} />
        <WorldTile testid="world-tile-oldmoney" to="/collections/old-money" img="/images/old_p1.jpg" title={t("oldmoney")} text={t("oldTile")} />
      </div>
    </section>
  );
};

const Selection = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetchProducts().then((all) => {
      const st = all.filter((p) => p.world === "street").slice(0, 2);
      const om = all.filter((p) => p.world === "oldmoney").slice(0, 2);
      setItems([st[0], om[0], st[1], om[1]].filter(Boolean));
    });
  }, []);
  if (!items.length) return null;
  return (
    <section data-testid="selection-section" className="relative bg-[var(--ivory)] text-[var(--ink)] px-5 md:px-10 py-24 md:py-36">
      <div className="flex items-end justify-between mb-14 md:mb-20">
        <RevealLines lines={[t("selection")]} className="f-serif italic text-6xl md:text-[7rem] leading-none" />
        <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600">(03) SS26</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-14">
        {items.map((p, i) => (
          <ProductCard key={p.id} p={p} index={i} total={items.length} className={i % 2 ? "lg:mt-24" : ""} />
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  const [ready, setReady] = useState(introPlayed);
  const done = useCallback(() => { introPlayed = true; setReady(true); }, []);
  return (
    <main data-testid="home-page">
      {!introPlayed && <Intro onDone={done} />}
      <SplitHero ready={ready} />
      <Marquee />
      <Manifesto />
      <Worlds />
      <Selection />
      <Footer />
    </main>
  );
}
